import { createActions, createReducer } from 'reduxsauce';
import {
	cloneDeep, get, set, isEmpty, forEach, findIndex, indexOf,
} from 'lodash';
import { locationDetails } from './initialState';

export { default as thunks } from './thunks';

/* ------------- Initial State ------------- */
export const INITIAL_STATE = {
	jobs            : [],
	jobEstimates    : [],
	expandedRowIds  : [],
	locations       : [],
	positionsData   : [],
	jobEvent        : {},
	counts          : {},
	notes           : {},
	totalCount      : 0,
	activeMapJob    : '',
	jobClockIns     : [],
	jobClockOuts    : [],
	jobPings        : [],
	skipJobsRequest : false,
	filter          : {
		locationId      : null,
		recurrenceId    : null,
		startDate       : null,
		endDate         : null,
		asc             : true,
		unStaffed       : false,
		includeInactive : false,
		displayCount    : 25,
		pageNumber      : 1,
		type            : 1,
		dateRange       : 7,
		sortBy          : 'Date',
		prePay          : null, // false will hide all prepay jobs completely
	},
	loaders : {
		jobPoints           : false,
		jobEstimates        : false,
		jobs                : false,
		details             : false,
		counts              : false,
		locations           : false,
		notes               : false,
		applicants          : false,
		officers            : false,
		locationDetailsFeed : false,
		jobHistoryModal     : false,
	},
	modals : {
		jobHistoryModal : {
			show        : false,
			showDetails : false,
			data        : [],
			detailsData : [],
			settings    : {
				jobId       : null,
				showDetails : false,
			},
		},
	},
	locationDetails,
};

/* ------------- Types and Action Creators ------------- */
export const { Types, Creators } = createActions({
	setModalOpen           : ['modal', 'value'],
	setModalSettings       : ['modal', 'settings'],
	resetModal             : ['modal'],
	receiveJobEvent        : ['jobEvent'],
	updateLoaders          : ['property', 'value'],
	receiveData            : ['collection'],
	updateFilters          : ['collection'],
	toggleDetailsExpansion : ['rowId'],
	clearJobEvent          : null,
	clearJobs              : null,
	clearCounts            : null,
	clearDetailsExpansion  : null,
	receiveNotes           : ['notes'],
	receivePositionData    : ['dataType', 'positionId', 'data'],
	receiveJobHistory      : ['data'],
	setJobHistoryDetails   : ['index'],
}, { prefix : '@jobs/' });

/* ------------- Hookup Reducers To Types ------------- */
export const reducer = createReducer(INITIAL_STATE, {
	[Types.RECEIVE_JOB_EVENT] : (state, { jobEvent }) => ({
		...state,
		jobEvent,
		positionsData : [],
	}),

	[Types.CLEAR_JOB_EVENT] : (state) => ({
		...state,
		jobEvent : INITIAL_STATE.jobEvent,
	}),

	[Types.CLEAR_JOBS] : (state) => ({
		...state,
		jobs : INITIAL_STATE.jobs,
	}),

	[Types.CLEAR_COUNTS] : (state) => ({
		...state,
		counts : INITIAL_STATE.counts,
	}),

	[Types.UPDATE_LOADERS] : (state, { property, value }) => {
		const newState = cloneDeep(state);

		set(newState, ['loaders', property], value);

		return newState;
	},

	[Types.RECEIVE_DATA] : (state, { collection }) => {
		if (!isEmpty(collection)) {
			const newState = cloneDeep(state);

			forEach(collection, ({ property, value }) => {
				set(newState, property, value);
			});

			return newState;
		}
		return state;
	},

	[Types.UPDATE_FILTERS] : (state, { collection }) => {
		const newState = cloneDeep(state);

		forEach(collection, ({ property, value }) => {
			set(newState, ['filter', property], value);
		});

		return newState;
	},

	[Types.TOGGLE_DETAILS_EXPANSION] : (state, { rowId }) => {
		const newState = cloneDeep(state);
		const index = indexOf(state.expandedRowIds, rowId);

		if (index === -1) newState.expandedRowIds.push(rowId);
		else newState.expandedRowIds.splice(index, 1);

		return newState;
	},

	[Types.CLEAR_DETAILS_EXPANSION] : (state) => ({
		...state,
		expandedRowIds : INITIAL_STATE.expandedRowIds,
	}),

	[Types.RECEIVE_NOTES] : (state, { notes }) => ({
		...state,
		notes,
	}),

	[Types.RECEIVE_POSITION_DATA] : (state, { dataType, positionId, data }) => {
		const newState = cloneDeep(state);
		const positionIndex = findIndex(newState.positionsData, ['positionId', positionId]);

		if (positionIndex > -1) set(newState, ['positionsData', positionIndex, dataType], data);
		else {
			newState.positionsData.push({
				positionId,
				[dataType] : data,
			});
		}

		return newState;
	},

	[Types.SET_MODAL_OPEN] : (state, { modal, value }) => {
		const newState = cloneDeep(state);

		set(newState, ['modals', modal, 'show'], value);

		return newState;
	},

	[Types.SET_MODAL_SETTINGS] : (state, { modal, settings }) => {
		const newState = cloneDeep(state);

		set(newState, ['modals', modal, 'settings'], settings);

		return newState;
	},

	[Types.RESET_MODAL] : (state, { modal }) => {
		const newState = cloneDeep(state);
		const initialModal = get(INITIAL_STATE, ['modals', modal]);
		set(newState, ['modals', modal], initialModal);

		return newState;
	},

	[Types.RECEIVE_JOB_HISTORY] : (state, { data }) => {
		const newState = cloneDeep(state);
		set(newState, ['modals', 'jobHistoryModal', 'data'], data);

		return newState;
	},

	[Types.SET_JOB_HISTORY_DETAILS] : (state, { index }) => {
		const newState = cloneDeep(state);
		const changes = get(state, ['modals', 'jobHistoryModal', 'data', index, 'Changes'], 'No changes');
		set(newState, ['modals', 'jobHistoryModal', 'detailsData'], changes);
		set(newState, ['modals', 'jobHistoryModal', 'detailsData'], changes);

		return newState;
	},
});

export default Creators;
